import api from '@/api/api';
import { useApiGet } from '@/api/apiRequests';
import { getParagraphs, getOrps } from '@/api/services';
import { PageLoader } from '@/components/PageLoader';
import { ParagraphItem } from '@/interfaces/Paragraphs';
import { OrpItem } from '@/interfaces/Orps';
import { AxiosError } from 'axios';
import { Info } from 'lucide-react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { MultiSelect, MultiSelectChangeEvent } from 'primereact/multiselect';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { FC, useState } from 'react';
import { toast } from 'react-toastify';

interface PromptResponse {
  // biome-ignore lint/style/useNamingConvention: API response
  system_prompt: string;
  // biome-ignore lint/style/useNamingConvention: API response
  user_prompt: string;
  response: string;
}

interface TableRowData {
  orp: string;
  charakter: string;
  hsou: boolean;
  paragraphText: string;
  // biome-ignore lint/style/useNamingConvention: API response
  system_prompt?: string;
  // biome-ignore lint/style/useNamingConvention: API response
  user_prompt?: string;
}

export const DebugPrompt: FC = () => {
  const [selectedParagraph, setSelectedParagraph] = useState<ParagraphItem | null>(null);
  const [selectedOrps, setSelectedOrps] = useState<OrpItem[]>([]);
  const [tableData, setTableData] = useState<TableRowData[]>([]);
  const [loadingPrompt, setLoadingPrompt] = useState<boolean>(false);
  const [dialogVisible, setDialogVisible] = useState<boolean>(false);
  const [promptData, setPromptData] = useState<PromptResponse | null>(null);
  const [selectedOrpForDialog, setSelectedOrpForDialog] = useState<string>('');
  const [paragraphDialogVisible, setParagraphDialogVisible] = useState<boolean>(false);
  const [selectedParagraphText, setSelectedParagraphText] = useState<string>('');

  const { data: paragraphsData, isLoading: paragraphsLoading } = useApiGet<ParagraphItem[], AxiosError>(
    ['paragraphs'],
    async () => await getParagraphs(),
    { enabled: true }
  );

  const { data: orpsData, isLoading: orpsLoading } = useApiGet<OrpItem[], AxiosError>(
    ['orps'],
    async () => await getOrps(),
    { enabled: true }
  );

  // Transform ORPs for MultiSelect
  const orpOptions = orpsData?.map((orp) => ({
    ...orp,
    label: orp.municipality?.name || orp.municipality?.orp_name || '',
  })) || [];

  const handleShowPrompt = async () => {
    if (!selectedParagraph?.id || selectedOrps.length === 0) {
      return;
    }

    setLoadingPrompt(true);
    setTableData([]);

    try {
      const results: TableRowData[] = [];

      for (const orp of selectedOrps) {
        try {
          const response = await api.post('/prompt/ask', {
            orp: orp.municipality?.name || orp.municipality?.orp_name || '',
            // biome-ignore lint/style/useNamingConvention: API param
            paragraph_id: selectedParagraph.id,
          });

          results.push({
            orp: orp.municipality?.name || orp.municipality?.orp_name || '',
            charakter: orp.charakter || '',
            hsou: orp.hsou || false,
            paragraphText: response.data?.response || '',
            system_prompt: response.data?.system_prompt || '',
            user_prompt: response.data?.user_prompt || '',
          });
        } catch (error: any) {
          results.push({
            orp: orp.municipality?.name || orp.municipality?.orp_name || '',
            charakter: orp.charakter || '',
            hsou: orp.hsou || false,
            paragraphText: `Error: ${error?.response?.data?.detail || 'Failed to generate paragraph'}`,
          });
        }
      }

      setTableData(results);
    } catch (error: any) {
      if (error?.response?.data?.detail) {
        toast.error(`Error: ${error.response.data.detail}`);
      } else {
        toast.error('Failed to generate paragraphs.');
      }
      console.error('Error generating paragraphs:', error);
    } finally {
      setLoadingPrompt(false);
    }
  };

  const handleShowDetails = (orpName: string) => {
    const rowData = tableData.find(row => row.orp === orpName);
    if (!rowData) {
      toast.error('Data not found for this ORP.');
      return;
    }

    setSelectedOrpForDialog(orpName);
    setPromptData({
      system_prompt: rowData.system_prompt || '',
      user_prompt: rowData.user_prompt || '',
      response: rowData.paragraphText || '',
    });
    setDialogVisible(true);
  };

  const hideDialog = () => {
    setDialogVisible(false);
    setPromptData(null);
  };

  const handleShowParagraphText = (text: string) => {
    setSelectedParagraphText(text);
    setParagraphDialogVisible(true);
  };

  const hideParagraphDialog = () => {
    setParagraphDialogVisible(false);
    setSelectedParagraphText('');
  };

  const hsouBodyTemplate = (rowData: TableRowData) => {
    return <span>{rowData.hsou ? 'Ano' : 'Ne'}</span>;
  };

  const paragraphTextBodyTemplate = (rowData: TableRowData) => {
    return (
      <div
        className="max-h-40 overflow-auto whitespace-pre-wrap text-sm cursor-pointer hover:bg-gray-100 p-1 rounded"
        onDoubleClick={() => handleShowParagraphText(rowData.paragraphText)}
        title="Dvojklik pro zobrazení celého textu"
      >
        {rowData.paragraphText}
      </div>
    );
  };

  const actionsBodyTemplate = (rowData: TableRowData) => {
    return (
      <Button
        label="Prompt details"
        icon="pi pi-eye"
        className="p-button-sm p-button-outlined"
        onClick={() => handleShowDetails(rowData.orp)}
      />
    );
  };

  const formatJson = (jsonString: string | undefined): string => {
    if (!jsonString) return '';
    try {
      const parsed = JSON.parse(jsonString);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return jsonString;
    }
  };

  const isLoading = paragraphsLoading || orpsLoading;
  const canSubmit = selectedParagraph && selectedOrps.length > 0;

  return (
    <>
      <h1 className={'text-2xl font-bold mb-8 text-white'}>Debug Prompt</h1>
      <div className={'bg-white rounded-lg p-6 shadow-sm'}>
        <div className="flex flex-col gap-6">
          <div className="flex gap-6">
            <div className="field flex-1">
              <label htmlFor="paragraph" className="font-bold block mb-2">
                Paragraph
              </label>
              <Dropdown
                id="paragraph"
                value={selectedParagraph}
                onChange={(e: DropdownChangeEvent) => setSelectedParagraph(e.value)}
                options={paragraphsData || []}
                optionLabel="name"
                filter={true}
                placeholder="Choose paragraph"
                className="w-full"
              />
            </div>

            <div className="field flex-1">
              <label htmlFor="orp" className="font-bold block mb-2">
                ORP{' '}
                <i
                  className="pi pi-info-circle text-blue-500 cursor-help"
                  title="Choose at least one ORP"
                />
              </label>
              <MultiSelect
                id="orp"
                value={selectedOrps}
                onChange={(e: MultiSelectChangeEvent) => setSelectedOrps(e.value)}
                options={orpOptions}
                optionLabel="label"
                filter={true}
                placeholder="Choose at least one ORP"
                className="w-full"
                display="chip"
                maxSelectedLabels={5}
              />
            </div>
          </div>

          <div className="flex justify-start">
            <Button
              label="Show"
              className="btn-primary"
              style={{ borderRadius: '6px' }}
              onClick={handleShowPrompt}
              disabled={!canSubmit}
              loading={loadingPrompt}
              title="This may take a moment – the paragraphs are being generated by the chatbot."
            />
          </div>
        </div>
      </div>

      {tableData.length > 0 && (
        <div className="bg-white rounded-lg p-6 shadow-sm mt-6">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            Results for paragraph: {selectedParagraph?.name}
            <span title="In the report, these paragraphs are reformulated to prevent unnecessary repetition of words.">
              <Info size={18} className="text-blue-500 cursor-help" />
            </span>
          </h2>
          <DataTable
            value={tableData}
            paginator
            rows={10}
            rowsPerPageOptions={[5, 10, 25, 50]}
            tableStyle={{ minWidth: '50rem' }}
            stripedRows
          >
            <Column field="orp" header="ORP" sortable style={{ width: '12%' }} />
            <Column field="charakter" header="Character" sortable style={{ width: '12%' }} />
            <Column field="hsou" header="HSOU" body={hsouBodyTemplate} sortable style={{ width: '8%' }} />
            <Column
              field="paragraphText"
              header="Paragraph Text"
              body={paragraphTextBodyTemplate}
              style={{ width: '43%' }}
            />
            <Column
              body={actionsBodyTemplate}
              style={{ width: '15%' }}
            />
          </DataTable>
        </div>
      )}

      <Dialog
        visible={dialogVisible}
        style={{ width: '90vw', maxWidth: '1400px', height: '80vh' }}
        contentStyle={{ height: '100%', display: 'flex', flexDirection: 'column' }}
        header={`Prompt: ${selectedParagraph?.name} - ${selectedOrpForDialog}`}
        modal={true}
        onHide={hideDialog}
        draggable={false}
        maximizable={true}
      >
        <div className="flex gap-4 flex-1 min-h-0">
          <div className="flex-1 flex flex-col min-w-0">
            <h3 className="font-bold text-lg mb-2 text-gray-700">System Prompt</h3>
            <div className="flex-1 overflow-auto bg-gray-50 p-4 rounded border border-gray-200">
              <pre className="whitespace-pre-wrap text-sm font-mono text-gray-800">
                {promptData?.system_prompt}
              </pre>
            </div>
          </div>
          <div className="flex-1 flex flex-col min-w-0">
            <h3 className="font-bold text-lg mb-2 text-gray-700">User Prompt</h3>
            <div className="flex-1 overflow-auto bg-gray-50 p-4 rounded border border-gray-200">
              <pre className="whitespace-pre-wrap text-sm font-mono text-gray-800">
                {formatJson(promptData?.user_prompt)}
              </pre>
            </div>
          </div>
        </div>
      </Dialog>

      <Dialog
        visible={paragraphDialogVisible}
        style={{ width: '70vw', maxWidth: '900px' }}
        header="Paragraph Text"
        modal={true}
        onHide={hideParagraphDialog}
        draggable={false}
        maximizable={true}
      >
        <div className="overflow-auto max-h-[70vh]">
          <p className="whitespace-pre-wrap text-sm">
            {selectedParagraphText}
          </p>
        </div>
      </Dialog>

      <PageLoader visible={isLoading} />
    </>
  );
};
